import { useState, useCallback } from "react";
import axios, { AxiosInstance, AxiosResponse } from "axios";

// Create a configured Axios instance
const apiClient: AxiosInstance = axios.create({
  // baseURL: "http://localhost:8080/zenith-portal/public/api",
  baseURL: "https://api.zenithsms.v2nportal.com/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Types for the hook parameters
type HttpMethod = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, string>;
  timeout?: number;
}

interface UseApiReturn<T = any> {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  request: (
    endpoint: string,
    method?: HttpMethod,
    body?: any,
    options?: RequestOptions
  ) => Promise<T | null>;
  get: (endpoint: string, options?: RequestOptions) => Promise<T | null>;
  post: (
    endpoint: string,
    body: any,
    options?: RequestOptions
  ) => Promise<T | null>;
  put: (
    endpoint: string,
    body: any,
    options?: RequestOptions
  ) => Promise<T | null>;
  del: (endpoint: string, options?: RequestOptions) => Promise<T | null>;
  patch: (
    endpoint: string,
    body: any,
    options?: RequestOptions
  ) => Promise<T | null>;
}

/**
 * A simplified API hook that returns raw responses
 */
export function useApi<T = any>(): UseApiReturn<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Helper to ensure endpoint doesn't start with a slash
  const normalizeEndpoint = useCallback((endpoint: string): string => {
    return endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
  }, []);

  // Main request function
  const request = useCallback(
    async (
      endpoint: string,
      method: HttpMethod = "GET",
      body?: any,
      options?: RequestOptions
    ): Promise<T | null> => {
      setIsLoading(true);
      setError(null);

      const normalizedEndpoint = normalizeEndpoint(endpoint);

      try {
        const response: AxiosResponse<T> = await apiClient.request({
          method: method.toLowerCase(),
          url: normalizedEndpoint,
          data: body,
          params: options?.params,
          headers: options?.headers,
          timeout: options?.timeout,
        });

        setData(response.data);
        return response.data;
      } catch (error) {
        // Even on error, return what we got if possible
        if (axios.isAxiosError(error) && error.response?.data) {
          setData(error.response.data as T);
          return error.response.data as T;
        }

        const errorObj =
          error instanceof Error ? error : new Error(String(error));
        setError(errorObj);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [normalizeEndpoint]
  );

  // Convenience methods for different HTTP methods
  const get = useCallback(
    (endpoint: string, options?: RequestOptions): Promise<T | null> => {
      return request(endpoint, "GET", undefined, options);
    },
    [request]
  );

  const post = useCallback(
    (
      endpoint: string,
      body: any,
      options?: RequestOptions
    ): Promise<T | null> => {
      return request(endpoint, "POST", body, options);
    },
    [request]
  );

  const put = useCallback(
    (
      endpoint: string,
      body: any,
      options?: RequestOptions
    ): Promise<T | null> => {
      return request(endpoint, "PUT", body, options);
    },
    [request]
  );

  const del = useCallback(
    (endpoint: string, options?: RequestOptions): Promise<T | null> => {
      return request(endpoint, "DELETE", undefined, options);
    },
    [request]
  );

  const patch = useCallback(
    (
      endpoint: string,
      body: any,
      options?: RequestOptions
    ): Promise<T | null> => {
      return request(endpoint, "PATCH", body, options);
    },
    [request]
  );

  return {
    data,
    error,
    isLoading,
    request,
    get,
    post,
    put,
    del,
    patch,
  };
}
